import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  Briefcase,
  LifeBuoy,
  CheckSquare,
  Users,
  ArrowRight,
  Loader2,
  Archive,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

/**
 * ConversationsBlock — Wave 19 #92.
 *
 * Customer profile → "Conversations" tab. Lists every chat thread the
 * current staff user is a member of for this customer (job-card,
 * ticket, task, manual). Loads once on mount; re-renders when the
 * customerId changes.
 *
 * Privacy: portal/client users are 403'd by `/chats/by-customer/{cid}`
 * server-side, and the entire CustomerDetail tab list is already gated
 * to staff so this block never renders for clients.
 */

const KIND_META = {
  jobcard: { Icon: Briefcase, label: "Job card", color: "#F59E0B" },
  ticket: { Icon: LifeBuoy, label: "Ticket", color: "#F43F5E" },
  task: { Icon: CheckSquare, label: "Task", color: "#10B981" },
  manual: { Icon: Users, label: "Group", color: "#A78BFA" },
};

function _whenLabel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    return d.toLocaleDateString();
  } catch (_) {
    return "";
  }
}

export default function ConversationsBlock({ customerId, customerName }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/chats/by-customer/${customerId}`);
        if (!alive) return;
        setRows(data || []);
      } catch (e) {
        if (!alive) return;
        toast.error(e?.response?.data?.detail || "Could not load chats.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [customerId]);

  const visible = rows.filter((r) => (showArchived ? true : !r.archived));
  const archivedCount = rows.filter((r) => r.archived).length;

  return (
    <div className="space-y-3" data-testid="customer-conversations-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#1fb6c1]" />
            Conversations
          </h2>
          <p className="text-xs text-[#a0aabf] mt-0.5">
            Every chat thread linked to {customerName || "this customer"} —
            jobs, tickets, tasks and group chats. Internal only; portal
            users never see these.
          </p>
        </div>
        {archivedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="customer-conversations-toggle-archived"
          >
            <Archive className="w-3.5 h-3.5" />
            {showArchived ? "Hide archived" : `Show archived (${archivedCount})`}
          </button>
        )}
      </div>

      {loading ? (
        <div className="hv-card p-6 text-sm text-[#a0aabf] flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading conversations…
        </div>
      ) : visible.length === 0 ? (
        <div
          className="hv-card p-8 text-center"
          data-testid="customer-conversations-empty"
        >
          <MessageCircle className="w-8 h-8 mx-auto text-[#647087] mb-3" />
          <div className="text-sm text-[#cbd2df] font-medium">
            No conversations yet.
          </div>
          <p className="text-xs text-[#a0aabf] mt-1.5 max-w-sm mx-auto">
            Job cards, tickets and tasks for this customer auto-create a
            chat thread when they're opened. You'll see them listed here.
          </p>
        </div>
      ) : (
        <div
          className="hv-card p-0 overflow-hidden divide-y divide-white/5"
          data-testid="customer-conversations-list"
        >
          {visible.map((t) => {
            const meta = KIND_META[t.kind] || KIND_META.manual;
            const Icon = meta.Icon;
            const memberCount = (t.member_ids || []).length;
            return (
              <Link
                to={`/chat/${t.id}`}
                key={t.id}
                className="flex items-start gap-3 p-3 hover:bg-white/[0.03] transition group"
                data-testid={`customer-conversation-${t.id}`}
              >
                <div
                  className="w-9 h-9 rounded-md border flex items-center justify-center shrink-0"
                  style={{
                    borderColor: `${meta.color}55`,
                    background: `${meta.color}1a`,
                  }}
                >
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded"
                      style={{
                        color: meta.color,
                        background: `${meta.color}15`,
                        border: `1px solid ${meta.color}33`,
                      }}
                    >
                      {meta.label}
                    </span>
                    <span className="text-sm font-medium text-white truncate">
                      {t.title}
                    </span>
                    {t.archived && (
                      <span className="text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-400/40 text-amber-200">
                        Archived
                      </span>
                    )}
                  </div>
                  {t.last_message_preview && (
                    <div className="text-xs text-[#a0aabf] truncate mt-0.5">
                      {t.last_message_preview}
                    </div>
                  )}
                  <div className="text-[10px] text-[#647087] mt-1 flex items-center gap-3">
                    <span>{memberCount} members</span>
                    {t.last_message_at && (
                      <span>{_whenLabel(t.last_message_at)}</span>
                    )}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#647087] group-hover:text-[#1fb6c1] transition shrink-0 mt-1" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
